import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import React, { ButtonHTMLAttributes, ReactElement } from 'react';
import VoxelTooltipShort from '../Tooltip/VoxelTooltipShort';
import VoxelLoadingSpinner from '../Animation/VoxelLoadingSpinner';

export interface DataTableToolbarButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'title' | 'color'> {
  iconProp?: IconProp;
  icon?: ReactElement<FontAwesomeIconProps>;
  label?: string;
  button?: boolean;
  toggle?: boolean;
  effect?: 'warning' | 'success' | 'error' | 'info' | 'primary';
  effectAnimate?: boolean;
  loading?: boolean;
  tooltip?: React.ReactNode;
}

export const DataTableToolbarButton = React.forwardRef<
  HTMLButtonElement,
  DataTableToolbarButtonProps
>(
  (
    {
      type = 'button',
      toggle,
      iconProp,
      icon,
      label,
      className,
      loading,
      button,
      tooltip,
      disabled,
      onClick,
      effect,
      effectAnimate = true,
      ...buttonProps
    },
    ref
  ) => {
    return (
      <VoxelTooltipShort message={tooltip} placement={'bottom'} closeOnClick showOnFocus={false}>
        <button
          ref={ref}
          onClick={(event) => {
            !disabled && onClick && onClick(event);
          }}
          className={`
          ${effect && effectAnimate && 'animate-pulse'}
          ${
            effect === 'primary' &&
            !toggle &&
            !disabled &&
            `
            bg-primary-300/80
            text-primary-800
            hover:bg-primary-300/70 hover:text-primary-700
            active:bg-primary-300/80
            `
          }
          ${
            effect === 'info' &&
            !toggle &&
            !disabled &&
            `
            bg-info-100
            text-info-700
            hover:bg-info-200 hover:text-info-700
            active:bg-info-200
            `
          }
          ${
            effect === 'success' &&
            !toggle &&
            !disabled &&
            `
            bg-success-600/20
            text-success-900
            hover:bg-success-600/30 hover:text-success-900
            active:bg-success-600/20
            `
          }
          ${
            effect === 'warning' &&
            !toggle &&
            !disabled &&
            `
            bg-warning-500/20
            text-warning-800
            hover:bg-warning-500/50 hover:text-warning-800
            active:bg-warning-300
            `
          }
          ${
            effect === 'error' &&
            !toggle &&
            !disabled &&
            `
            bg-error-100
            text-error-700
            hover:bg-error-200 hover:text-error-700
            active:bg-error-200
            `
          }
        
          ${
            !effect &&
            !toggle &&
            !disabled &&
            !button &&
            `
          text-voxel-gray-500
          hover:bg-voxel-gray-200 hover:text-voxel-gray-600
          active:bg-voxel-gray-200
            `
          }

          ${
            toggle &&
            !button &&
            !disabled &&
            `
            bg-voxel-gray-500
            text-voxel-gray-50
            `
          }
        ${
          button &&
          !toggle &&
          !disabled &&
          `
            bg-primary-200
            text-primary-800
            hover:bg-primary-300/80 hover:text-primary-700
            active:bg-primary-300
            `
        }

        ${!disabled && 'cursor-pointer'}
        ${disabled && 'cursor-default'}
        ${disabled && 'bg-voxel-gray-50'}
        ${disabled && 'text-voxel-gray-300'}
        
        px-2.5 h-7 rounded
        flex flex-row items-center flex-nowrap
        whitespace-nowrap
        ${className}
      `}
          {...buttonProps}
        >
          {icon && icon}
          {iconProp && <FontAwesomeIcon icon={iconProp} size="1x" />}
          {loading && !label && (
            <div className="absolute transform scale-[2] -ml-[2.5px] opacity-40">
              <VoxelLoadingSpinner />
            </div>
          )}
          {!!label && <span className="pl-1.5 pr-0.5 text-xs">{label}</span>}
        </button>
      </VoxelTooltipShort>
    );
  }
);

export default DataTableToolbarButton;
